/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { NewRelicDecoratedError } from "utilities/noticeError";

/*
Client for interacting with Floodgate P13N service. Typically, we want to use the feature flags we query
from our backend (FloodgateService). These get populated on the `currentUser` object and can be easily
referenced using the `useCurrentUser` hook from anywhere in our React app.

However, in some situations it may be useful to query Floodgate directly from the browser, for instance,
to help provide messaging via the `GlobalAlertBanner` if our servers are down, or in features pertaining
to unauthenticated users (who can't interact with our server).
 */
export class FloodgateClientError extends NewRelicDecoratedError {
  constructor(errorOrString: Error | string, statusCode: number) {
    super(errorOrString, { statusCode });
    this.name = "FloodgateClientError";
  }
}

export class FloodgateClient {
  private readonly baseUrl: string;
  private readonly clientId: string;
  private readonly adobeIOApiKey: string;
  private readonly imsOrgId: string;
  private readonly sandboxName: string;

  private cachedResponse: FeaturesResponse | null = null;
  private fetchPromise: Promise<FeaturesResponse | null> | null = null;

  constructor(
    baseURL: string,
    clientId: string,
    adobeIOApiKey: string,
    imsOrgId: string,
    sandboxName: string
  ) {
    this.baseUrl = baseURL;
    this.clientId = clientId;
    this.adobeIOApiKey = adobeIOApiKey;
    this.imsOrgId = imsOrgId;
    this.sandboxName = sandboxName;
  }

  async *getFeatures(): AsyncIterable<FeaturesResponse> {
    if (this.cachedResponse) {
      // yielding a cached version prevents flicker as the user navigates around the site
      yield this.cachedResponse;
    }

    if (!this.fetchPromise) {
      this.fetchPromise = this.fetchFeatures();
    }

    const response = await this.fetchPromise;
    if (response) {
      // when navigating make sure we refetch to pick up latest changes
      this.fetchPromise = null;
      yield response;
    }
  }

  private async fetchFeatures(): Promise<FeaturesResponse | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}?clientId=${this.clientId}&meta=true`,
        {
          headers: {
            ["X-Api-Key"]: this.adobeIOApiKey,
            ["x-gw-ims-org-id"]: this.imsOrgId,
            ["x-sandbox-name"]: this.sandboxName,
          },
        }
      );

      if (response.ok) {
        const responseJSON = await response.json();
        this.cachedResponse = responseJSON;
        return responseJSON;
      } else {
        throw new FloodgateClientError(
          "Invalid response from floodgate service",
          response.status
        );
      }
    } catch (error) {
      newrelic.addPageAction("FLOODGATE_API_ERROR", {
        errorName: error.name,
        errorMessage: error.message,
        online: String(window.navigator.onLine),
        unloaded: String(window.unloaded),
      });
      return null;
    }
  }

  async *getFeatureMetadata(
    featureKey: string
  ): AsyncIterable<string | undefined> {
    for await (const features of this.getFeatures()) {
      const metas = features.releases.flatMap((release) => release.meta);
      const encodedParam = metas.find(({ k }) => k === featureKey)?.v;

      if (encodedParam) {
        yield atob(encodedParam);
      } else {
        yield undefined;
      }
    }
  }

  async *getFeatureEnabled(featureKey: string): AsyncIterable<boolean> {
    for await (const features of this.getFeatures()) {
      yield features.releases.some((release) =>
        release.features.includes(featureKey)
      );
    }
  }
}

export namespace FloodgateApi {
  export const instance = new FloodgateClient(
    process.env.FLOODGATE_API_URL!,
    process.env.IMS_CLIENT_ID!,
    process.env.ADOBE_IO_API_KEY!,
    process.env.FLOODGATE_IMS_ORG!,
    process.env.FLOODGATE_SANDBOX_NAME!
  );
}

export interface FeaturesResponse {
  releases: {
    features: string[];
    meta: {
      k: string;
      v: string;
    }[];
  }[];
}
